import { AppDispatch } from '@/app/store';
import { AuthorDTO } from '@/types/AuthorDTO';
import { ErrorDTO } from '@/types/ErrorDTO';
import { Grid, Pagination, useMediaQuery } from '@mui/material';
import { InboxList } from '@/components/home/inbox/InboxList';
import { InboxTable } from '@/components/home/inbox/InboxTable';
import { LetterRequestDTO } from '@/types/letter/LetterRequestDTO';
import { LetterTableDTO } from '@/types/letter/LetterTableDTO';
import { NavigateFunction, useNavigate } from 'react-router-dom';
import { OutlinedField } from '@/components/common/field/OutlinedField';
import {
  ROUTE_CREATE_LETTER_AUTHOR,
  ROUTE_CREATE_LETTER_CLIENT,
  getCreateLetterReplyRoute,
  getInboxLetterRoute,
} from '@/app/routes';
import { SenderInfoDTO } from '@/types/create/SenderInfoDTO';
import { SortRequestDTO } from '@/types/page/SortRequestDTO';
import { Theme } from '@mui/material/styles';
import { UserDTO } from '@/types/user/UserDTO';
import { formatUserFullName } from '@/utils/string-utils';
import { getCurrentUser } from '@/services/authSlice';
import {
  getLetterInboxRequest,
  setLetterInboxRequestPageNumber,
  setLetterInboxRequestPageSize,
  setLetterInboxSearchRequestAddressee,
  setLetterInboxSortRequest,
} from '@/services/letter/letterInboxSlice';
import { setAuthor, setCreateInfoFromDTO, setPhoneNumber } from '@/services/createFormSlice';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { useDebounce } from '@/app/hooks/useDebounce';
import { useGetCurrentUserQuery } from '@/services/api/authApiSlice';
import { useGetLettersQuery, useSetLetterReadMutation } from '@/services/api/letterApiSlice';
import { useGetSenderToReplyMutation } from '@/services/api/createFormApiSlice';
import { useSnackbar } from 'notistack';
import { validateAuthorFull, validateSender } from '@/utils/validation-utils';
import React, { ChangeEvent, FC, ReactElement, useEffect, useState } from 'react';

export const InboxBlock: FC = (): ReactElement => {
  const dispatch: AppDispatch = useAppDispatch();
  const navigate: NavigateFunction = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const request: LetterRequestDTO = useAppSelector(getLetterInboxRequest);
  const authUser: UserDTO | undefined = useAppSelector(getCurrentUser);

  const { data: user } = useGetCurrentUserQuery(authUser?.id, {
    skip: !authUser,
  });
  const { data, isFetching, isLoading } = useGetLettersQuery({ ...request, userId: authUser?.id }, { skip: !authUser });

  const [getSender] = useGetSenderToReplyMutation();
  const [readLetter] = useSetLetterReadMutation();

  const [search, setSearch] = useState<string>('');

  const isMobile: boolean = useMediaQuery((theme: Theme): string => theme.breakpoints.down('sm'));

  const handlePage = (page: number): void => {
    dispatch(setLetterInboxRequestPageNumber(page - 1));
  };

  const handleAddressee = useDebounce<string>((addressee: string): void => {
    dispatch(setLetterInboxSearchRequestAddressee(addressee));
    dispatch(setLetterInboxRequestPageNumber(0));
  }, 500);

  const handleSearch = (addressee: string): void => {
    setSearch(addressee);
    handleAddressee(addressee);
  };

  const handleSort = (sortRequest: SortRequestDTO): void => {
    dispatch(setLetterInboxSortRequest(sortRequest));
  };

  const handleLetterClick = async (letter: LetterTableDTO): Promise<void> => {
    navigate(getInboxLetterRoute(letter.id));
    if (letter.unread) {
      await readLetter(letter.id)
        .unwrap()
        .catch((e: { status: number; data: ErrorDTO }) => {
          enqueueSnackbar(e.data.message, { variant: 'error' });
        });
    }
  };

  const handleReplyClick = async (letter: LetterTableDTO): Promise<void> => {
    await getSender(letter.id)
      .unwrap()
      .then((dto: SenderInfoDTO): void => {
        let author: AuthorDTO | null = null;
        dispatch(setCreateInfoFromDTO(dto));
        if (user) {
          author = {
            name: formatUserFullName(user),
            phone: user.phone || '',
            email: user.email,
          };
          dispatch(setAuthor(author));
          if (!user?.phone) {
            dispatch(setPhoneNumber(dto.authorPhone));
          }
        }
        if (!validateSender(dto.sender)) {
          navigate(ROUTE_CREATE_LETTER_CLIENT);
        } else if (!validateAuthorFull(author)) {
          navigate(ROUTE_CREATE_LETTER_AUTHOR);
        } else {
          navigate(getCreateLetterReplyRoute(letter.id));
        }
      });
  };

  useEffect((): void => {
    dispatch(setLetterInboxRequestPageSize(isMobile ? 5 : 8));
  }, [dispatch, isMobile]);

  useEffect((): void => {
    setSearch(request.searchRequest.addressee || '');
  }, [request.searchRequest.addressee]);

  return (
    <Grid container={true} direction={'column'} spacing={2}>
      <Grid item={true}>
        <OutlinedField
          fullWidth={true}
          size={'small'}
          placeholder={'Поиск по адресату'}
          name={'addressee'}
          value={search}
          onChange={(e: ChangeEvent<HTMLInputElement>): void => {
            handleSearch(e.target.value);
          }}
          onClear={(): void => {
            handleSearch('');
          }}
        />
      </Grid>
      <Grid item={true}>
        {isMobile ? (
          <InboxList
            isFetching={isFetching}
            isLoading={isLoading}
            letters={data?.content || []}
            onLetterClick={handleLetterClick}
          />
        ) : (
          <InboxTable
            isFetching={isFetching}
            isLoading={isLoading}
            letters={data?.content || []}
            sortRequest={
              request.pageRequest.sortRequest || {
                properties: ['answer.processedAt', 'answerDate'],
                direction: 'DESC',
              }
            }
            onSort={handleSort}
            onLetterClick={handleLetterClick}
            onReplyClick={handleReplyClick}
          />
        )}
      </Grid>
      <Grid item={true} display={'flex'} justifyContent={isMobile ? 'center' : 'right'}>
        <Pagination
          count={data?.totalPages || 1}
          page={(request.pageRequest?.pageNumber || 0) + 1}
          onChange={(e: ChangeEvent<unknown>, page: number): void => {
            handlePage(page);
          }}
        />
      </Grid>
    </Grid>
  );
};
